import React, { useEffect, useState } from 'react';
import { isEnabledSeaBattleByMetashop } from '~/utils/category';
import { settings } from '~/utils/settings';
import { useDispatch, useSelector } from 'react-redux';
import { hiddenMenuItem, setFetching, setFullscreen } from '~/Actions/ActionApp';
import { State } from '~/Reducers';
import equal from 'fast-deep-equal/react';
import { getStaticUrl, redirectToLogin } from '~/utils/utils';
import { setScrollover } from '~/api/WoWsClient';
import History from '~/utils/history';
import { shipyardSoundOff, shipyardSoundOn } from '@wg/web2clientapi/sound';
import { urlsMap } from '~/utils/menu';
import { showDownloadGamePopup } from '~/components/Popups/settings';
import { CATEGORIES } from '~/const';

const SeaBattle = React.lazy(() => import('@wg/seabattle/app'));

interface IStateSelector {
    isEnabled: boolean;
    hasActiveSeason: boolean;
    isActivatedUser: boolean;
}

const stateSelector = (state: State): IStateSelector => {
    return {
        isEnabled: state.ReducerSeaBattle.isEnabled,
        hasActiveSeason: state.ReducerSeaBattle.hasActiveSeason,
        isActivatedUser: state.ReducerSeaBattle.isActivatedUser,
    };
};

const accountIdStateSelector = (state: State): number | null => {
    return state.ReducerAccount?.id;
};

const SeaBattleContainer: React.FC<unknown> = () => {
    const { hasActiveSeason, isActivatedUser, isEnabled: isEnabledByState } = useSelector<State, IStateSelector>(stateSelector, equal);
    const accountId = useSelector<State, number>(accountIdStateSelector, equal);
    const [isStaticLoaded, setIsStaticLoaded] = useState(false);
    const dispatch = useDispatch();
    const settingsOverrides = { publicPath: getStaticUrl(''), languageCode: settings.languageCode };
    const isEnabled = isEnabledSeaBattleByMetashop() && isEnabledByState;
    const goBack = () => History.backHome();
    const onClose = (permanent: boolean) => {
        if (permanent) {
            dispatch(hiddenMenuItem(CATEGORIES.SEA_BATTLE));
        }
        goBack();
    };

    useEffect(() => {
        if (!accountId) {
            dispatch(setFetching(true));
            dispatch(setFullscreen(true));
            redirectToLogin();
        }

        if (isEnabled) {
            dispatch(setFetching(true));
            dispatch(setFullscreen(true));
            shipyardSoundOff();
            import('@wg/seabattle/style.css').then(() => {
                setIsStaticLoaded(true);
            });
        }

        if (accountId && hasActiveSeason && !isActivatedUser) {
            showDownloadGamePopup({ onClose: goBack });
        }

        return () => {
            shipyardSoundOn();
            dispatch(setFullscreen(false));
            setScrollover(false);
        };
    }, [hasActiveSeason, isActivatedUser, isEnabled, accountId]);

    const hideLoadingScreen = React.useCallback(() => {
        setScrollover(true);
        dispatch(setFetching(false));
    }, [dispatch]);

    if (!hasActiveSeason) {
        History.navigate(urlsMap.home);
        return;
    }

    return (
        isEnabled &&
        isStaticLoaded && (
            <React.Suspense fallback={<></>}>
                <SeaBattle url={settings.urls.seaBattleSettings} onInitialized={hideLoadingScreen} onClose={onClose} onBuyTicket={() => onClose(false)} settingsOverrides={settingsOverrides} />
            </React.Suspense>
        )
    );
};

export default SeaBattleContainer;
